import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Sparkles, Clock, Coins } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Section } from "./ProfileFieldPrimitives";
import AmbassadorBadge from "@/components/AmbassadorBadge";

/**
 * AmbassadorSection — inline panel inside the profile editor.
 *
 * States:
 *   none      → "Apply to be a Panda Ambassador" CTA with pitch textarea
 *   applied   → "Application under review" banner + editable pitch
 *   approved  → tier badge + next-drop countdown + benefits list
 *   revoked   → muted "no longer active" banner + re-apply CTA
 */
export default function AmbassadorSection() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pitch, setPitch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/ambassador/me");
      setState(data);
      if (data.pitch) setPitch((prev) => prev || data.pitch);
    } catch {
      setState({ status: "none", tier: null });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const apply = async () => {
    if (submitting || pitch.trim().length < 20) {
      toast.error("Tell us at least 20 characters about why you'd be a great ambassador.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/ambassador/apply", { pitch: pitch.trim() });
      toast.success("Application in — we'll ping you when it's reviewed.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Section icon={Sparkles} title="Panda Ambassador">
      <div className="p-4 space-y-3" data-testid="ambassador-section">
        {loading && (
          <div className="flex items-center justify-center py-4 text-zinc-500">
            <Loader2 size={14} className="animate-spin mr-2" /> Loading…
          </div>
        )}

        {!loading && state?.status === "approved" && (
          <ApprovedView state={state} />
        )}

        {!loading && state?.status === "applied" && (
          <>
            <div className="text-xs uppercase tracking-widest text-amber-400 flex items-center gap-1">
              <Clock size={12} /> Application under review
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Thanks for applying! Our team reviews within a few days. You can update your
              pitch below in the meantime.
            </p>
            <PitchInput pitch={pitch} setPitch={setPitch} />
            <button
              type="button"
              onClick={apply}
              disabled={submitting}
              data-testid="ambassador-update-pitch"
              className="text-xs text-rose-400 hover:text-rose-300"
            >
              {submitting ? "Saving…" : "Update pitch"}
            </button>
          </>
        )}

        {!loading && (state?.status === "none" || state?.status === "revoked") && (
          <>
            {state.status === "revoked" && (
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Previous status revoked — you can re-apply anytime.
              </div>
            )}
            <p className="text-sm text-zinc-400 leading-relaxed">
              Ambassadors get a golden badge, a monthly <b>{state.monthly_drop_coins} coin</b> drop,
              and boosted visibility. Tell us why you'd be a great fit.
            </p>
            <Link
              to="/ambassadors/apply"
              data-testid="ambassador-learn-more-link"
              className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 underline underline-offset-2"
            >
              Read the full program details →
            </Link>
            <PitchInput pitch={pitch} setPitch={setPitch} />
            <button
              type="button"
              onClick={apply}
              disabled={submitting || pitch.trim().length < 20}
              data-testid="ambassador-apply-button"
              className="w-full bg-gradient-to-r from-yellow-400 to-amber-500 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold rounded-full py-2.5 text-sm flex items-center justify-center gap-2"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Applying…</> : <><Sparkles size={14} /> Apply to be an Ambassador</>}
            </button>
          </>
        )}
      </div>
    </Section>
  );
}

function PitchInput({ pitch, setPitch }) {
  return (
    <div>
      <textarea
        data-testid="ambassador-pitch-input"
        value={pitch}
        onChange={(e) => setPitch(e.target.value.slice(0, 1000))}
        placeholder="Why should you be a Panda Ambassador? (min 20 chars)"
        rows={4}
        className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-white text-sm focus:border-amber-400 outline-none resize-none"
      />
      <div className="text-[10px] text-zinc-600 text-right mt-1">
        {pitch.length}/1000
      </div>
    </div>
  );
}

function ApprovedView({ state }) {
  const nextDrop = state.next_drop_at ? new Date(state.next_drop_at) : null;
  const daysUntil = nextDrop
    ? Math.max(0, Math.ceil((nextDrop.getTime() - Date.now()) / (24 * 3600 * 1000)))
    : null;
  return (
    <div className="space-y-3">
      <AmbassadorBadge status="approved" tier={state.tier} size="lg" />
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-black/40 border border-white/10 rounded-md px-3 py-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Referrals</div>
          <div className="text-lg text-white font-display leading-tight">
            {state.referrals_count.toLocaleString()}
          </div>
        </div>
        <div className="bg-black/40 border border-white/10 rounded-md px-3 py-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
            <Coins size={10} /> Next drop
          </div>
          <div className="text-lg text-white font-display leading-tight">
            {daysUntil != null ? `${daysUntil}d` : "—"}
          </div>
        </div>
      </div>
      <p className="text-xs text-zinc-400 leading-relaxed">
        Tier auto-upgrades as your referral count grows —{" "}
        <b className="text-zinc-200">Bronze</b> → 20 refs → <b className="text-zinc-200">Silver</b> → 50 refs → <b className="text-zinc-200">Gold</b>.
      </p>
      <Link
        to="/ambassador/dashboard"
        data-testid="ambassador-open-dashboard-link"
        className="inline-flex items-center gap-1.5 bg-gradient-to-r from-yellow-400 to-amber-500 hover:brightness-110 text-black font-semibold rounded-full px-4 py-2 text-xs w-full justify-center"
      >
        Open Ambassador dashboard →
      </Link>
    </div>
  );
}
